import React, { useState } from 'react';
import { Button } from 'primereact/button';

interface GlobalButtonProps {
  label: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'outline';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  iconPos?: 'left' | 'right';
  fullWidth?: boolean;
  rounded?: boolean;
}

export const GlobalButton: React.FC<GlobalButtonProps> = ({
  label,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  icon,
  iconPos = 'left',
  fullWidth = false,
  rounded = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getVariantStyles = (): {
    background: string;
    color: string;
    boxShadow: string;
    border?: string;
  } => {
    const variants = {
      primary: {
        background: loading || disabled
          ? 'linear-gradient(135deg, #5a7d7e 0%, #3d5a5b 100%)'
          : isHovered
          ? 'linear-gradient(135deg, #1a5555 0%, #0f3d3e 100%)'
          : 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
        color: loading || disabled ? 'rgba(255,255,255,0.7)' : 'white',
        boxShadow: loading || disabled
          ? 'none'
          : isHovered
          ? '0 12px 35px rgba(15, 61, 62, 0.4)'
          : '0 8px 25px rgba(15, 61, 62, 0.3)',
      },
      secondary: {
        background: loading || disabled
          ? 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)'
          : isHovered
          ? 'linear-gradient(135deg, #4b5563 0%, #6b7280 100%)'
          : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
        color: 'white',
        boxShadow: loading || disabled
          ? 'none'
          : isHovered
          ? '0 12px 35px rgba(107, 114, 128, 0.4)'
          : '0 8px 25px rgba(107, 114, 128, 0.3)',
      },
      success: {
        background: loading || disabled
          ? 'linear-gradient(135deg, #b8d88f 0%, #9abb6f 100%)'
          : isHovered
          ? 'linear-gradient(135deg, #b8d060 0%, #d4f17e 100%)'
          : 'linear-gradient(135deg, #d4f17e 0%, #b8d060 100%)',
        color: loading || disabled ? 'rgba(15, 61, 62, 0.5)' : '#0f3d3e',
        boxShadow: loading || disabled
          ? 'none'
          : isHovered
          ? '0 12px 35px rgba(212, 241, 126, 0.4)'
          : '0 8px 25px rgba(212, 241, 126, 0.3)',
      },
      danger: {
        background: loading || disabled
          ? 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)'
          : isHovered
          ? 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)'
          : 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
        color: 'white',
        boxShadow: loading || disabled
          ? 'none'
          : isHovered
          ? '0 12px 35px rgba(220, 38, 38, 0.4)'
          : '0 8px 25px rgba(220, 38, 38, 0.3)',
      },
      warning: {
        background: loading || disabled
          ? 'linear-gradient(135deg, #fcd34d 0%, #fbbf24 100%)'
          : isHovered
          ? 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)'
          : 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
        color: 'white',
        boxShadow: loading || disabled
          ? 'none'
          : isHovered
          ? '0 12px 35px rgba(245, 158, 11, 0.4)'
          : '0 8px 25px rgba(245, 158, 11, 0.3)',
      },
      outline: {
        background: isHovered ? 'rgba(15, 61, 62, 0.05)' : 'transparent',
        color: disabled ? '#9ca3af' : '#0f3d3e',
        border: `1px solid ${disabled ? '#e5e7eb' : '#0f3d3e'}`,
        boxShadow: 'none',
      },
    };
    return variants[variant];
  };

  const getSizeStyles = () => {
    const sizes = {
      small: {
        padding: '8px 24px',
        fontSize: '0.8rem',
      },
      medium: {
        padding: '12px 32px',
        fontSize: '0.9rem',
      },
      large: {
        padding: '14px 40px',
        fontSize: '1rem',
      },
    };
    return sizes[size];
  };

  const variantStyles = getVariantStyles();
  const sizeStyles = getSizeStyles();

  return (
    <Button
      label={label}
      onClick={onClick}
      type={type}
      disabled={disabled || loading}
      loading={loading}
      icon={icon}
      iconPos={iconPos}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...variantStyles,
        ...sizeStyles,
        width: fullWidth ? '100%' : 'auto',
        border: variantStyles.border || 'none',
        borderRadius: rounded ? '12px' : '6px',
        fontWeight: 600,
        cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
        transform: (loading || disabled || !isHovered) ? 'translateY(0)' : 'translateY(-2px)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: disabled ? 0.6 : 1,
      }}
      className="global-button"
    />
  );
};